// 難易度 (2026-07-25 ユーザー裁定)。 タイトルの「幕を選ぶ」で選択し、 localStorage に永続化する。
//   千秋楽 (senshuraku) = 既存の高難度。 このモジュールは全て base を返す = 挙動を1bitも変えない。
//   初演   (hatsuen)    = 主人公だけを緩める救済モード。 ボス側の数値には一切触らない。
//     - HP  : ×2 (ユーザー指定「一旦2倍」)。
//     - 体幹 : 崩れにくく = 体幹上限 (stun しきい値) を上げ、 同じ自体幹ゲインの占める割合を下げる (×1.5)。
//   econ.ts と同じ「module-level 可変 + getter」パターン。 setDifficulty は
//   ゲーム開始 (難易度確定・game.ts) と起動時/state 生成時 (loadSettings・state.ts/main.ts) に呼ぶ。
import type { Difficulty } from './types'
import { PLAYER, PLAYER_DAMAGE } from './balance'
import { finisherDamageFor } from './finisher_damage'

// 倍率は名前付き定数で1箇所に。 実機の体感で調整する前提 (ユーザー playtest 後に触る)。
const HATSUEN_HP_MUL = 2
const HATSUEN_POSTURE_MUL = 1.5

// 初演: 標準(20)を超える分だけを圧縮して「大抵の被弾=命灯半分」へ寄せる。
//   1メモリ=40HP なので、標準20がちょうど半分。重い技だけが基準から浮いていたのを畳む。
const HATSUEN_HEAVY_COMPRESS = 0.5   // ✅確定 (2026-07-28 実機「ちょうどいい」・台帳)

// 既定は初演 (2026-10-09・persistence.ts DEFAULT_SETTINGS と同じ)。 実際の値は state.ts が loadSettings で毎回合わせる。
let current: Difficulty = 'hatsuen'

export function setDifficulty(d: Difficulty): void {
  current = d
}
export function getDifficulty(): Difficulty {
  return current
}

// 主人公の実効最大HP。 千秋楽=base のまま (既存挙動を変えない)。 初演=×2。
export function playerMaxHp(): number {
  return current === 'hatsuen' ? Math.round(PLAYER.maxHp * HATSUEN_HP_MUL) : PLAYER.maxHp
}

// 主人公の実効最大体幹 (崩れ=stun のしきい値)。 千秋楽=base のまま。 初演=上限UPで崩れにくく。
export function playerMaxPosture(): number {
  return current === 'hatsuen' ? Math.round(PLAYER.maxPosture * HATSUEN_POSTURE_MUL) : PLAYER.maxPosture
}

/**
 * 主人公が受ける HP ダメージの実効値。
 *   千秋楽 = base をそのまま返す (既存挙動を1bitも変えない)。
 *   初演   = 標準ダメージ(20)以下は不変。超過分だけ HATSUEN_HEAVY_COMPRESS 倍に畳む。
 *            ボスの致命 (bossFinisher) は圧縮しない (票 §4-b)。
 * ⚠️ 体幹 (postureDamage) には使わない。パリィの比率を変えないため。
 */
export function playerIncomingDamage(base: number, opts?: { bossFinisher?: boolean }): number {
  // 致命 (共有の 60) だけ、その難易度の実効の最大体力の 4 割 (10-18 ユーザー・src/finisher_damage.ts)。千秋楽 40・初演 80。圧縮はしない。
  //   DEV ?finisherDmg=0|off|60 で旧 (60 のまま)・40|35|25 で固定。
  if (opts?.bossFinisher) return finisherDamageFor(base, playerMaxHp())
  if (current !== 'hatsuen') return base
  const std = PLAYER_DAMAGE.standard
  if (base <= std) return base
  return Math.round(std + (base - std) * HATSUEN_HEAVY_COMPRESS)
}

// ===== 初演の「時間」の救済 (DEV ?hatsuenTime=1・2026-10-09 夜・比較版) =====
// 今の初演の救済は量 (体力・体幹) だけで、 パリィの受付と回避の無敵の長さは千秋楽と同じ。
//   反応が少し遅れる環境 (Bluetooth のパッド・テレビ) では量の救済が効かない、 という点検
//   (docs/research/2026-10-09-difficulty-baseline-draft.md) から。
//   ここは主人公側の時間だけを広げる。 ボス側の数値 (技の長さ・当たりの窓・予兆) には一切触らない
//   (ユーザーの決まり「ボスの読みやすさは下げない。 救済は難易度とガード側で作る」)。
//   配布版 = このフラグは立たない (main.ts の DEV の箱の中でだけ立てる) ＝ どの関数も base をそのまま返す。
//   千秋楽でも効かない (hatsuenTimeReliefActive が初演を見る)。
export const HATSUEN_TIME_PARRY_BONUS_MS = 40         // パリィの受付 200ms → 240ms
export const HATSUEN_TIME_DODGE_IFRAME_MS = 150       // 回避の無敵を 150ms 以上へ (既定 200ms〈10-14〜〉は既にこれ以上なので下げない＝効くのは DEV ?dodgeIFrame=c の 100ms の時)

let hatsuenTimeRelief = false
export function setHatsuenTimeRelief(on: boolean): void {
  hatsuenTimeRelief = on
}
/** 初演で、 しかも時間の救済が立っている時だけ true。 千秋楽では常に false。 */
export function hatsuenTimeReliefActive(): boolean {
  return hatsuenTimeRelief && current === 'hatsuen'
}

/** 主人公のパリィの受付 (ms)。 base = PLAYER.parryActiveMs。 連打で縮む前のフル窓。 */
export function playerParryActiveMs(): number {
  return PLAYER.parryActiveMs + (hatsuenTimeReliefActive() ? HATSUEN_TIME_PARRY_BONUS_MS : 0)
}
/** 主人公の回避の無敵の下限 (ms)。 救済が立っていない時は 0 = 何も足さない。 */
export function hatsuenDodgeIFrameFloorMs(): number {
  return hatsuenTimeReliefActive() ? HATSUEN_TIME_DODGE_IFRAME_MS : 0
}

// ===== 一戦だけモードの難易度 (DEV ?soloDiff=senshuraku|hatsuen・2026-10-09 夜) =====
// 指定なし = 今どおり初演 (SOLO_DIFFICULTY・state.ts)。 読むのは main.ts の DEV の箱の中だけ = 配布版は常に初演。
export function parseSoloDifficulty(raw: string | null): Difficulty | null {
  return raw === 'senshuraku' || raw === 'hatsuen' ? raw : null
}
